import { Pin, Plus, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/components/ui/cn';
import { EntityChipIcon, type EntityKind } from '@/components/ui/entityIcons';

/*
  WorkspaceTabs — полоса открытых объектов (уровень навигации 3, «как в BILLmanager»).
  Это ВЕРХНИЙ край холста: полоса на muted-фоне, активная вкладка сливается с содержимым.
  Справа (trailing) живут общие инструменты холста — поиск и команды ⌘K.

  Устройство вкладки: настоящая <button role="tab"> (фокус-кольцо только с клавиатуры и внутрь) и
  соседняя кнопка «закрыть» — вложенных интерактивных элементов нет. Линия под полосой — внутренняя
  тень строки (shadow-hairline-b), активная вкладка просто перекрывает её своим фоном: никаких
  выступающих псевдоэлементов, значит, никаких неожиданных полос прокрутки.
  Закреплённые — без крестика; средняя кнопка закрывает. Переполнение и Ctrl+Tab — далее.
*/

export interface WorkspaceTab {
  id: string;
  kind: EntityKind;
  title: string;
  pinned?: boolean;
  /** Класс точки состояния (bg-sev-4, bg-success …). */
  dotClassName?: string;
}

interface WorkspaceTabsProps {
  tabs: WorkspaceTab[];
  activeId: string;
  onSelect: (id: string) => void;
  onClose?: (id: string) => void;
  onNew?: () => void;
  trailing?: ReactNode;
  className?: string;
}

export function WorkspaceTabs({
  tabs,
  activeId,
  onSelect,
  onClose,
  onNew,
  trailing,
  className,
}: WorkspaceTabsProps) {
  return (
    <div
      data-slot="workspace-tabs"
      className={cn('flex h-11 shrink-0 items-end gap-1 bg-tabs pl-2 shadow-hairline-b', className)}
    >
      <div
        role="tablist"
        aria-label="Открытые объекты"
        className="scrollbar-none flex min-w-0 items-end gap-1 overflow-x-auto"
      >
        {tabs.map((t) => {
          const active = t.id === activeId;
          return (
            <div key={t.id} role="presentation" className="group/tab relative shrink-0">
              <button
                type="button"
                role="tab"
                aria-selected={active}
                title={t.title}
                onClick={() => onSelect(t.id)}
                onAuxClick={(e) => {
                  if (e.button === 1 && !t.pinned) onClose?.(t.id);
                }}
                className={cn(
                  'flex h-8 max-w-56 items-center gap-1.5 rounded-t-sm border border-b-0 pl-3 text-xs select-none',
                  t.pinned || !onClose ? 'pr-3' : 'pr-7',
                  'transition-colors duration-fast focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset',
                  active
                    ? 'border-line bg-canvas text-fg'
                    : 'border-transparent text-fg-muted hover:bg-hover hover:text-fg',
                )}
              >
                <EntityChipIcon kind={t.kind} className="size-3.5 shrink-0 text-fg-subtle" />
                <span className="truncate font-medium">{t.title}</span>
                {t.dotClassName && (
                  <span className={cn('size-1.5 shrink-0 rounded-full', t.dotClassName)} aria-hidden />
                )}
                {t.pinned && <Pin className="size-3 shrink-0 text-fg-subtle" aria-label="Закреплена" />}
              </button>
              {!t.pinned && onClose && (
                <button
                  type="button"
                  aria-label={`Закрыть ${t.title}`}
                  onClick={() => onClose(t.id)}
                  className={cn(
                    'absolute top-1/2 right-1.5 grid size-4 -translate-y-1/2 place-items-center rounded-xs text-fg-subtle',
                    'opacity-0 transition-opacity duration-fast group-hover/tab:opacity-100 hover:bg-active hover:text-fg',
                    'focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                    active && 'opacity-100',
                  )}
                >
                  <X className="size-3" />
                </button>
              )}
            </div>
          );
        })}
        {onNew && (
          <button
            type="button"
            aria-label="Открыть объект"
            onClick={onNew}
            className="mb-1 grid size-6 shrink-0 place-items-center rounded-xs text-fg-subtle transition-colors duration-fast hover:bg-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            <Plus className="size-3.5" />
          </button>
        )}
      </div>
      {trailing && (
        <div className="ml-auto flex shrink-0 items-center gap-2 self-center pr-3">{trailing}</div>
      )}
    </div>
  );
}
